/**
 * Zona para elegir o arrastrar una imagen, en el backoffice (ADM-04).
 *
 * Es el mismo control en los dos lugares donde el panel sube imagenes —las
 * fotos de un producto y la pieza de un banner— y por eso vive aca: son la
 * misma interaccion, y duplicarla habria dejado dos zonas de arrastre que se
 * comportan distinto.
 *
 * No sabe nada de productos ni de banners: recibe el archivo y avisa. Quien lo
 * usa decide a donde lo manda y que hace con la respuesta.
 *
 * **El archivo se manda tal cual, sin tocarlo.** Es lo contrario de lo que hace
 * el buscador por foto, que lo achica en el navegador antes de subir, y es a
 * proposito: alla la foto se usa y se tira, aca se PUBLICA, y una imagen
 * reducida dos veces —una por el lienzo del navegador y otra por el servidor—
 * pierde calidad para nada. El que convierte, endereza y mide es el servidor,
 * que ademas es el unico al que no se puede mentir.
 *
 * El `<input type="file">` es uno de verdad, escondido detras de la etiqueta:
 * es lo unico que abre el selector del sistema, y con `htmlFor` la etiqueta
 * entera funciona con teclado sin escribir un solo manejador.
 */
import { useId, useState } from 'react';
import type { DragEvent } from 'react';

import { Icono } from '../comunes/Icono';

export interface SubidorImagenProps {
  /** Texto de la zona. Cambia con lo que se sube: una foto, una pieza. */
  etiqueta: string;
  /** Linea chica de abajo: que medida conviene, que formato. */
  ayuda?: string;
  /** Mientras sube, la zona no acepta otro archivo. */
  subiendo: boolean;
  /** Tipos que ofrece el selector del sistema. Por defecto, cualquier imagen. */
  acepta?: string;
  /** Lo que dice mientras sube y en la zona de arrastre. */
  que?: string;
  onArchivo(archivo: File): void;
}

export function SubidorImagen({
  etiqueta,
  ayuda,
  subiendo,
  acepta = 'image/*',
  que = 'una imagen',
  onArchivo,
}: SubidorImagenProps) {
  const id = useId();
  const [arrastrando, setArrastrando] = useState(false);

  function soltar(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setArrastrando(false);
    if (subiendo) return;

    const archivo = evento.dataTransfer.files[0];
    if (archivo !== undefined) onArchivo(archivo);
  }

  return (
    <div
      className={arrastrando ? 'subidor arrastrando' : 'subidor'}
      onDragOver={(evento) => {
        evento.preventDefault();
        setArrastrando(true);
      }}
      onDragLeave={() => {
        setArrastrando(false);
      }}
      onDrop={soltar}
    >
      <input
        id={id}
        className="solo-lectores"
        type="file"
        accept={acepta}
        disabled={subiendo}
        onChange={(evento) => {
          const archivo = evento.target.files?.[0];
          if (archivo !== undefined) onArchivo(archivo);
          // Deja volver a elegir el MISMO archivo: sin esto el `change` no se
          // dispara la segunda vez y reintentar una subida fallida no funciona.
          evento.target.value = '';
        }}
      />

      <Icono nombre="galeria" />
      {subiendo ? (
        <p role="status">Subiendo {que}…</p>
      ) : (
        <p>
          Arrastra {que} hasta aca, o{' '}
          <label className="subidor-enlace" htmlFor={id}>
            {etiqueta}
          </label>
        </p>
      )}
      {ayuda !== undefined ? <p className="subidor-nota">{ayuda}</p> : null}
    </div>
  );
}
